import { useEffect, useRef, useState, useCallback } from 'react';
import {
    Plus,
    Trash2,
    Image as ImageIcon,
    Loader2,
    Upload,
    X,
    GripVertical,
    Check,
    AlertCircle,
} from 'lucide-react';
import { motion, AnimatePresence, Reorder } from 'framer-motion';
import { uploadImage } from '@/lib/cloudinary';
import {
    getImages,
    saveImages,
    addImage,
    removeImage,
    loadMigratedImages,
} from '@/lib/imageStore';
import { useSupabase } from '@/providers/SupabaseProvider';

const CATEGORIES = [
    { key: 'kakoos', label: 'Kakoos' },
    { key: 'peircings', label: 'Piercings' },
    { key: 'availablesketches', label: 'Available Sketches' },
    { key: 'covers', label: 'קאברים' },
    { key: 'hair', label: 'הדמיות שיער' },
];

function ImageCard({ image, onDelete, isDragging }) {
    return (
        <motion.div
            layout
            className={`group relative rounded-xl aspect-square overflow-hidden bg-neutral-800 ${
                isDragging
                    ? 'ring-2 ring-green-500 ring-offset-2 ring-offset-neutral-900'
                    : ''
            }`}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
        >
            <img
                src={image.src}
                alt=''
                className='w-full h-full object-cover'
                draggable={false}
            />
            <div className='absolute inset-0 bg-gradient-to-t from-black/60 via-transparent opacity-0 group-hover:opacity-100 transition-opacity' />
            <div className='top-2 left-2 absolute opacity-0 group-hover:opacity-100 transition-opacity'>
                <div className='flex items-center gap-1 bg-neutral-900/80 backdrop-blur-sm p-1.5 rounded-lg'>
                    <GripVertical size={14} className='text-neutral-400' />
                </div>
            </div>
            <button
                onClick={() => onDelete(image.public_id)}
                className='top-2 right-2 absolute bg-red-600/90 hover:bg-red-500 opacity-0 group-hover:opacity-100 p-2 rounded-full hover:scale-110 transition-all transform'
            >
                <Trash2 size={16} />
            </button>
            <div className='right-2 bottom-2 absolute opacity-0 group-hover:opacity-100 transition-opacity'>
                <span className='bg-black/50 px-2 py-1 rounded text-white/70 text-xs'>
                    {image.src.split('/').pop()?.slice(0, 15)}...
                </span>
            </div>
        </motion.div>
    );
}

function DropZone({ onFilesDropped, isDragOver, setIsDragOver }) {
    return (
        <div
            onDragOver={(e) => {
                e.preventDefault();
                setIsDragOver(true);
            }}
            onDragLeave={() => setIsDragOver(false)}
            onDrop={(e) => {
                e.preventDefault();
                setIsDragOver(false);
                const files = Array.from(e.dataTransfer.files).filter((f) =>
                    f.type.startsWith('image/'),
                );
                if (files.length > 0) onFilesDropped(files);
            }}
            className={`border-2 border-dashed rounded-xl p-8 text-center transition-all ${
                isDragOver
                    ? 'border-green-500 bg-green-500/10'
                    : 'border-neutral-600 hover:border-neutral-500 bg-neutral-800/50'
            }`}
        >
            <Upload
                size={32}
                className={`mx-auto mb-3 ${isDragOver ? 'text-green-500' : 'text-neutral-500'}`}
            />
            <p className='font-medium text-neutral-300'>
                Drop images here to upload
            </p>
            <p className='mt-1 text-neutral-500 text-sm'>
                or use the button below
            </p>
        </div>
    );
}

function UploadModal({
    isOpen,
    onClose,
    onUpload,
    activeCategory,
    uploading,
    uploadProgress,
}) {
    const [localDragOver, setLocalDragOver] = useState(false);
    const fileInputRef = useRef(null);

    if (!isOpen) return null;

    const handleFiles = (files) => {
        const imageFiles = Array.from(files).filter((f) =>
            f.type.startsWith('image/'),
        );
        onUpload(imageFiles);
    };

    return (
        <AnimatePresence>
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className='z-50 fixed inset-0 flex justify-center items-center bg-black/80 backdrop-blur-sm p-4'
                onClick={onClose}
            >
                <motion.div
                    initial={{ scale: 0.95, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0.95, opacity: 0 }}
                    className='bg-neutral-900 p-6 border border-neutral-700 rounded-2xl w-full max-w-lg'
                    onClick={(e) => e.stopPropagation()}
                >
                    <div className='flex justify-between items-center mb-4'>
                        <h2 className='font-bold text-white text-xl'>
                            Upload Images
                        </h2>
                        <button
                            onClick={onClose}
                            className='hover:bg-neutral-800 p-2 rounded-lg transition'
                        >
                            <X size={20} />
                        </button>
                    </div>

                    <div className='mb-4'>
                        <span className='text-neutral-400 text-sm'>
                            Uploading to:{' '}
                        </span>
                        <span className='font-medium text-green-400 text-sm'>
                            {
                                CATEGORIES.find((c) => c.key === activeCategory)
                                    ?.label
                            }
                        </span>
                    </div>

                    <div
                        onDragOver={(e) => {
                            e.preventDefault();
                            setLocalDragOver(true);
                        }}
                        onDragLeave={() => setLocalDragOver(false)}
                        onDrop={(e) => {
                            e.preventDefault();
                            setLocalDragOver(false);
                            handleFiles(e.dataTransfer.files);
                        }}
                        onClick={() => fileInputRef.current?.click()}
                        className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all ${
                            localDragOver
                                ? 'border-green-500 bg-green-500/10'
                                : 'border-neutral-600 hover:border-green-500 hover:bg-neutral-800'
                        }`}
                    >
                        <div className='flex flex-col items-center'>
                            <div
                                className={`p-3 rounded-full mb-3 ${localDragOver ? 'bg-green-500/20' : 'bg-neutral-800'}`}
                            >
                                <Upload
                                    size={28}
                                    className={
                                        localDragOver
                                            ? 'text-green-500'
                                            : 'text-neutral-400'
                                    }
                                />
                            </div>
                            <p className='font-medium text-neutral-300'>
                                {localDragOver
                                    ? 'Drop files here!'
                                    : 'Drag & drop images here'}
                            </p>
                            <p className='mt-1 text-neutral-500 text-sm'>
                                or click to browse
                            </p>
                        </div>
                    </div>

                    <input
                        ref={fileInputRef}
                        type='file'
                        accept='image/*'
                        multiple
                        hidden
                        onChange={(e) => handleFiles(e.target.files)}
                    />

                    <div className='flex gap-3 mt-4'>
                        <button
                            onClick={() => fileInputRef.current?.click()}
                            className='flex flex-1 justify-center items-center gap-2 bg-green-600 hover:bg-green-500 py-3 rounded-xl font-medium text-white transition'
                        >
                            <Plus size={18} />
                            Select Files
                        </button>
                    </div>

                    {uploading && (
                        <div className='mt-6'>
                            <div className='flex justify-between mb-2 text-sm'>
                                <span className='text-neutral-400'>
                                    Uploading...
                                </span>
                                <span className='text-green-400'>
                                    {uploadProgress}%
                                </span>
                            </div>
                            <div className='bg-neutral-800 rounded-full h-2 overflow-hidden'>
                                <motion.div
                                    className='bg-green-500 h-full'
                                    initial={{ width: 0 }}
                                    animate={{ width: `${uploadProgress}%` }}
                                />
                            </div>
                        </div>
                    )}
                </motion.div>
            </motion.div>
        </AnimatePresence>
    );
}

export default function AdminGalleryDashboard() {
    useEffect(() => {
        const pwd = prompt('Enter password');
        if (pwd !== 'RazAdmin2024!') {
            window.location.href = '/';
        }
    }, []);

    const { supabase } = useSupabase();
    const [activeCategory, setActiveCategory] = useState(CATEGORIES[0].key);
    const [images, setImages] = useState({});
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
    const [isDragOverPage, setIsDragOverPage] = useState(false);
    const [reorderMode, setReorderMode] = useState(false);
    const [showUploadTip, setShowUploadTip] = useState(true);
    const [uploadResults, setUploadResults] = useState({
        success: 0,
        failed: 0,
    });

    const inputRef = useRef(null);
    const uploadRef = useRef(null);

    const loadGallery = useCallback(async () => {
        if (!supabase) return;
        const storedImages = await getImages(supabase);
        setImages(storedImages);
    }, [supabase]);

    useEffect(() => {
        if (!supabase) return;
        async function init() {
            setLoading(true);
            const storedImages = await loadMigratedImages(supabase);
            setImages(storedImages);
            setLoading(false);
        }
        init();
    }, [supabase]);

    const handleBulkUpload = useCallback(
        async (files) => {
            if (files.length === 0) return;
            if (!supabase) {
                console.error('Supabase not ready');
                return;
            }

            setUploading(true);
            setUploadProgress(0);
            setUploadResults({ success: 0, failed: 0 });
            setShowUploadTip(false);

            const totalFiles = files.length;
            let completed = 0;

            for (const file of files) {
                try {
                    const result = await uploadImage(file, activeCategory);
                    await addImage(supabase, activeCategory, result);
                    setUploadResults((prev) => ({
                        ...prev,
                        success: prev.success + 1,
                    }));
                } catch (error) {
                    console.error('Upload failed:', error);
                    setUploadResults((prev) => ({
                        ...prev,
                        failed: prev.failed + 1,
                    }));
                }
                completed++;
                setUploadProgress(Math.round((completed / totalFiles) * 100));
            }

            await loadGallery();
            setUploading(false);
            setIsUploadModalOpen(false);

            setTimeout(() => {
                setUploadResults({ success: 0, failed: 0 });
            }, 3000);
        },
        [activeCategory, loadGallery, supabase],
    );

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        uploadRef.current = handleBulkUpload;
    }, [handleBulkUpload]);

    useEffect(() => {
        const handleGlobalDragOver = (e) => {
            e.preventDefault();
            if (e.dataTransfer.types.includes('Files')) {
                setIsDragOverPage(true);
            }
        };
        const handleGlobalDragLeave = (e) => {
            if (e.relatedTarget === null) {
                setIsDragOverPage(false);
            }
        };
        const handleGlobalDrop = (e) => {
            e.preventDefault();
            setIsDragOverPage(false);
            const files = Array.from(e.dataTransfer.files).filter((f) =>
                f.type.startsWith('image/'),
            );
            if (files.length > 0 && uploadRef.current) {
                uploadRef.current(files);
            }
        };

        window.addEventListener('dragover', handleGlobalDragOver);
        window.addEventListener('dragleave', handleGlobalDragLeave);
        window.addEventListener('drop', handleGlobalDrop);

        return () => {
            window.removeEventListener('dragover', handleGlobalDragOver);
            window.removeEventListener('dragleave', handleGlobalDragLeave);
            window.removeEventListener('drop', handleGlobalDrop);
        };
    }, []);

    async function handleDelete(publicId) {
        if (!confirm('Delete this image from the gallery?')) {
            return;
        }
        try {
            await removeImage(supabase, activeCategory, publicId);
            await loadGallery();
        } catch (e) {
            console.error('Delete failed', e);
            alert('Failed to delete image. Check console.');
        }
    }

    function handleReorder(newOrder) {
        setImages((prev) => ({
            ...prev,
            [activeCategory]: newOrder,
        }));
    }

    async function saveReorder() {
        try {
            await saveImages(supabase, images);
            setReorderMode(false);
            await loadGallery();
        } catch (e) {
            console.error('Reorder save failed', e);
            alert('Failed to save order. Check console.');
        }
    }

    const currentImages = images[activeCategory] || [];

    return (
        <div
            className={`min-h-screen transition-colors ${isDragOverPage ? 'bg-green-900/20' : 'bg-neutral-900'}`}
        >
            <AnimatePresence>
                {isDragOverPage && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className='z-40 fixed inset-0 flex justify-center items-center bg-green-900/30 backdrop-blur-sm border-4 border-green-500 border-dashed pointer-events-none'
                    >
                        <div className='bg-neutral-900/90 p-8 rounded-2xl text-center'>
                            <Upload
                                size={48}
                                className='mx-auto mb-4 text-green-500'
                            />
                            <p className='font-bold text-white text-2xl'>
                                Drop images to upload
                            </p>
                            <p className='mt-2 text-neutral-400'>
                                Uploading to{' '}
                                <span className='text-green-400'>
                                    {
                                        CATEGORIES.find(
                                            (c) => c.key === activeCategory,
                                        )?.label
                                    }
                                </span>
                            </p>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            <div className='mx-auto p-4 md:p-6 max-w-7xl'>
                <div className='flex sm:flex-row flex-col justify-between items-start sm:items-center gap-4 mb-6'>
                    <h1 className='font-bold text-white text-2xl md:text-3xl'>
                        Gallery Manager
                    </h1>

                    <div className='flex items-center gap-2'>
                        {reorderMode ? (
                            <>
                                <button
                                    onClick={saveReorder}
                                    className='flex items-center gap-2 bg-green-600 hover:bg-green-500 px-4 py-2 rounded-lg font-medium text-white transition'
                                >
                                    <Check size={18} />
                                    Save Order
                                </button>
                                <button
                                    onClick={() => {
                                        setReorderMode(false);
                                        loadGallery();
                                    }}
                                    className='flex items-center gap-2 bg-neutral-700 hover:bg-neutral-600 px-4 py-2 rounded-lg font-medium text-white transition'
                                >
                                    <X size={18} />
                                    Cancel
                                </button>
                            </>
                        ) : (
                            <>
                                <button
                                    onClick={() => setReorderMode(true)}
                                    className='flex items-center gap-2 bg-neutral-700 hover:bg-neutral-600 px-4 py-2 rounded-lg font-medium text-white transition'
                                >
                                    <GripVertical size={18} />
                                    Reorder
                                </button>
                                <button
                                    onClick={() => setIsUploadModalOpen(true)}
                                    disabled={uploading}
                                    className='flex items-center gap-2 bg-green-600 hover:bg-green-500 disabled:bg-green-800 px-4 py-2 rounded-lg font-medium text-white transition'
                                >
                                    {uploading ? (
                                        <>
                                            <Loader2
                                                size={18}
                                                className='animate-spin'
                                            />
                                            Uploading...
                                        </>
                                    ) : (
                                        <>
                                            <Plus size={18} />
                                            Upload
                                        </>
                                    )}
                                </button>
                            </>
                        )}
                    </div>
                </div>

                <div className='-mx-4 mb-6 px-4 overflow-x-auto'>
                    <div className='flex gap-2 bg-neutral-800/50 p-1.5 rounded-xl min-w-max'>
                        {CATEGORIES.map((cat) => (
                            <button
                                key={cat.key}
                                onClick={() => setActiveCategory(cat.key)}
                                className={`px-4 py-2 rounded-lg text-sm font-semibold transition whitespace-nowrap ${
                                    activeCategory === cat.key
                                        ? 'bg-white text-black'
                                        : 'text-neutral-400 hover:text-white hover:bg-neutral-700'
                                }`}
                            >
                                {cat.label}
                            </button>
                        ))}
                    </div>
                </div>

                {showUploadTip && !loading && currentImages.length === 0 && (
                    <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className='flex items-start gap-3 bg-blue-500/10 mb-6 p-4 border border-blue-500/30 rounded-xl'
                    >
                        <AlertCircle
                            size={20}
                            className='flex-shrink-0 mt-0.5 text-blue-400'
                        />
                        <div>
                            <p className='font-medium text-blue-300'>
                                Upload Tip
                            </p>
                            <p className='mt-1 text-blue-400/70 text-sm'>
                                Drag and drop images anywhere on this page to
                                quick-upload, or click the Upload button above
                                for bulk upload.
                            </p>
                        </div>
                        <button
                            onClick={() => setShowUploadTip(false)}
                            className='ml-auto text-blue-400/50 hover:text-blue-300'
                        >
                            <X size={16} />
                        </button>
                    </motion.div>
                )}

                <AnimatePresence>
                    {uploadResults.success > 0 && (
                        <motion.div
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                            className='flex items-center gap-3 bg-green-500/10 mb-6 p-4 border border-green-500/30 rounded-xl'
                        >
                            <Check size={20} className='text-green-400' />
                            <p className='text-green-300'>
                                Successfully uploaded {uploadResults.success}{' '}
                                image{uploadResults.success > 1 ? 's' : ''}
                                {uploadResults.failed > 0 &&
                                    `, ${uploadResults.failed} failed`}
                            </p>
                        </motion.div>
                    )}
                </AnimatePresence>

                {loading ? (
                    <div className='flex justify-center items-center py-20'>
                        <Loader2
                            size={32}
                            className='text-neutral-500 animate-spin'
                        />
                    </div>
                ) : reorderMode ? (
                    <div className='bg-neutral-800/30 p-4 rounded-2xl'>
                        <p className='flex items-center gap-2 mb-4 text-neutral-400 text-sm'>
                            <GripVertical size={16} />
                            Drag images to reorder. Changes are saved when you
                            click "Save Order".
                        </p>
                        <Reorder.Group
                            axis='y'
                            values={currentImages}
                            onReorder={handleReorder}
                            className='gap-4 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6'
                        >
                            {currentImages.map((img) => (
                                <Reorder.Item
                                    key={img.public_id}
                                    value={img}
                                    className='cursor-grab active:cursor-grabbing'
                                >
                                    <ImageCard
                                        image={img}
                                        onDelete={handleDelete}
                                        isDragging={false}
                                    />
                                </Reorder.Item>
                            ))}
                        </Reorder.Group>
                    </div>
                ) : currentImages.length ? (
                    <motion.div
                        layout
                        className='gap-3 md:gap-4 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6'
                    >
                        <AnimatePresence mode='popLayout'>
                            {currentImages.map((img) => (
                                <motion.div
                                    key={img.public_id}
                                    layout
                                    initial={{ opacity: 0, scale: 0.8 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    exit={{ opacity: 0, scale: 0.8 }}
                                    transition={{ duration: 0.2 }}
                                >
                                    <ImageCard
                                        image={img}
                                        onDelete={handleDelete}
                                        isDragging={false}
                                    />
                                </motion.div>
                            ))}
                        </AnimatePresence>
                    </motion.div>
                ) : (
                    <div className='flex flex-col justify-center items-center py-20 text-neutral-500'>
                        <div className='bg-neutral-800/50 mb-4 p-6 rounded-full'>
                            <ImageIcon size={40} className='text-neutral-600' />
                        </div>
                        <p className='text-neutral-400 text-lg'>
                            No images in this category
                        </p>
                        <p className='mt-2 text-neutral-500 text-sm'>
                            Click Upload or drag images anywhere to add some
                        </p>
                    </div>
                )}
            </div>

            <UploadModal
                isOpen={isUploadModalOpen}
                onClose={() => setIsUploadModalOpen(false)}
                onUpload={handleBulkUpload}
                activeCategory={activeCategory}
                uploading={uploading}
                uploadProgress={uploadProgress}
            />
        </div>
    );
}
